import type {
  AuditEvent,
  PermissionDefinition,
  PermissionGrant,
  ProfileVersion,
  Scope,
  StaffAccess,
  StaffProposal,
} from './types';

export const moduleLabels: Record<string, string> = {
  iam: 'Administration des accès',
  author: 'Author',
  support: 'Support',
  backoffice: 'Backoffice',
  evals: 'Évaluations IA',
  prompts: 'Prompts et branches',
};

export const riskLabels: Record<PermissionDefinition['risk'], string> = {
  read: 'Lecture',
  write: 'Écriture',
  sensitive: 'Sensible',
};

const scopeKindLabels: Record<Scope['kind'], string> = {
  global: 'global',
  project: 'projet',
  branch: 'branche',
  supportChannel: 'canal support',
};

export function scopeLabel(scope: Scope): string {
  if (scope.kind === 'global') return 'Tous les périmètres';
  if (scope.kind === 'project') return `Projet · ${scope.projectId}`;
  if (scope.kind === 'branch') return `Branche · ${scope.projectId} / ${scope.branchId}`;
  return `Support · ${scope.channel}`;
}

export function scopeKindsLabel(kinds: Scope['kind'][]): string {
  return kinds.map(kind => scopeKindLabels[kind] || kind).join(' · ');
}

export function statusLabel(status: string): string {
  return status === 'active' ? 'Actif' : status === 'suspended' ? 'Suspendu' : 'Sans accès';
}

export function formatDate(timestamp: number): string {
  return Number.isFinite(timestamp) ? new Intl.DateTimeFormat('fr-FR', {dateStyle: 'medium', timeStyle: 'short'}).format(new Date(timestamp)) : '—';
}

export function toolsFor(staff: StaffAccess | null, effective: PermissionGrant[]): string[] {
  if (!staff || staff.status !== 'active') return [];
  return [...new Set(effective.map(grant => grant.permission.split('.')[0]).filter(module => module !== 'iam'))].sort();
}

export function isAdmin(staff: StaffAccess | null | undefined): boolean {
  return staff?.schemaVersion === 2 && staff.admin === true;
}

export function cloneStaff(staff: StaffAccess | null, uid: string): StaffProposal {
  return staff ? {
    status: staff.status,
    assignments: staff.assignments.map(item => ({...item, scope: {...item.scope}})),
    delegation: staff.delegation.map(item => ({...item, scope: {...item.scope}})),
    ...(staff.schemaVersion === 2 ? {admin: staff.admin} : {}),
  } : {status: 'active', assignments: [], delegation: []};
}

export function nextAssignmentId(existing: {assignmentId: string}[]): string {
  const used = new Set(existing.map(item => item.assignmentId));
  for (let index = 1; index <= 65; index += 1) {
    const id = `assignment-${index}`;
    if (!used.has(id)) return id;
  }
  return crypto.randomUUID();
}

export function permissionDiff(before: string[], after: string[]): {added: string[]; removed: string[]} {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  return {
    added: after.filter(permission => !beforeSet.has(permission)).sort(),
    removed: before.filter(permission => !afterSet.has(permission)).sort(),
  };
}

/* ------------------------------------------------------------------ identités */

/** Initiales issues du nom ou de l'e-mail, jamais de l'UID : un UID ne se reconnaît pas. */
export function initialsFrom(name?: string | null, email?: string | null): string {
  const source = (name || '').trim() || (email || '').trim();
  if (!source) return '?';
  const parts = source.replace(/[@._-]+/g, ' ').split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

export interface Identity {
  uid: string;
  email: string | null;
  displayName: string | null;
  resolved: boolean;
}

export function unresolvedIdentity(uid: string): Identity {
  return {uid, email: null, displayName: null, resolved: false};
}

export function identityPrimary(identity: Identity): string {
  return identity.displayName || identity.email || '';
}

export function identitySecondary(identity: Identity): string | null {
  return identity.displayName && identity.email ? identity.email : null;
}

/**
 * Cache d'identités par UID, vidé à chaque rotation de session. `GET /users` ne renvoie pas
 * l'e-mail : ce qui a déjà été résolu une fois sert partout, sans nouvel appel.
 */
const identityCache = new Map<string, Identity>();

export function rememberIdentity(identity: Identity): void {
  if (identity.resolved) identityCache.set(identity.uid, identity);
}

export function cachedIdentity(uid: string): Identity | undefined {
  return identityCache.get(uid);
}

export function cachedIdentities(): Map<string, Identity> {
  return new Map(identityCache);
}

export function clearIdentityCache(): void {
  identityCache.clear();
}

/** Exécute `worker` sur chaque entrée avec au plus `limit` appels en vol. Les échecs ne bloquent rien. */
export async function mapWithLimit<T, R>(items: T[], limit: number, worker: (item: T) => Promise<R>): Promise<Array<R | null>> {
  const results = new Array<R | null>(items.length).fill(null);
  const size = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;
  const runners = Array.from({length: size}, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      try {
        results[index] = await worker(items[index]);
      } catch {
        results[index] = null;
      }
    }
  });
  await Promise.all(runners);
  return results;
}

/* ------------------------------------------------------------------ permissions */

/** Traductions FR du catalogue v1. Repli sur la description anglaise de l'API pour toute nouvelle permission. */
export const permissionDescriptionsFr: Record<string, string> = {
  'evals.use': 'Utiliser le système d’evals : gérer les cas, lancer des appels IA payants et consulter les résultats.',
  'prompts.branches.read': 'Lire l’inventaire publié, les branches privées et les consommateurs enregistrés.',
  'prompts.branches.write': 'Créer, modifier et archiver des branches privées.',
  'prompts.promotions.prepare': 'Préparer une promotion de branche sans la publier.',
  'prompts.promotions.publish': 'Publier une promotion de branche approuvée.',
  'iam.users.read': 'Lire les comptes staff et leurs affectations',
  'iam.users.manage': 'Changer statut et affectations, dans le plafond de délégation',
  'iam.profiles.read': 'Lire les versions de profil',
  'iam.profiles.create': 'Créer une version de profil, dans le plafond',
  'iam.audit.read': "Lire l'historique des accès",
  'author.entry': "Entrer dans Author ; n'ouvre aucun contenu",
  'author.create': 'Créer un projet',
  'author.read': 'Lire le contenu, sous réserve des ACL projet et branche',
  'author.write': 'Modifier le contenu, sous réserve des ACL projet et branche',
  'author.manageAccess': "Gérer les accès d'un projet dans Author",
  'author.private.read': "Outrepasser la restriction de lecture d'une branche privée ; exige aussi author.read",
  'author.private.write': "Outrepasser la restriction d'écriture d'une branche privée ; exige aussi author.write",
  'author.share': 'Partager une branche, sous réserve des ACL',
  'author.archive': 'Archiver un projet ou une branche, sous réserve des ACL',
  'author.publish.staging': 'Publier en pré-production, sous réserve des contrôles de publication',
  'author.publish.production': 'Publier en production, sous réserve des contrôles de publication',
  'author.project.read.any': "Outrepasser l'ACL de lecture d'un projet ; exige aussi author.read",
  'author.project.write.any': "Outrepasser l'ACL d'écriture d'un projet ; exige aussi author.write",
  'author.project.manageAccess.any': "Outrepasser l'ACL de gestion d'accès ; exige aussi author.manageAccess",
  'author.branch.share.any': "Outrepasser l'ACL de partage d'une branche ; exige aussi author.share",
  'author.branch.archive.any': "Outrepasser l'ACL d'archivage d'une branche ; exige aussi author.archive",
  'author.publish.staging.any': "Outrepasser l'ACL de publication en pré-production ; contrôles de publication maintenus",
  'author.publish.production.any': "Outrepasser l'ACL de publication production ; contrôles de publication maintenus",
  'author.jobs.read': "Lire l'état et les résultats des traitements Author, sous réserve des ACL",
  'author.jobs.create': 'Lancer des traitements Author, sous réserve des ACL',
  'author.jobs.cancel': 'Annuler des traitements Author, sous réserve des ACL',
  'author.assets.read': 'Lire les ressources Author, sous réserve des ACL',
  'author.assets.write': 'Téléverser ou modifier les ressources Author, sous réserve des ACL',
  'support.read': 'Lire les dossiers de support',
  'support.prepare': 'Préparer des actions de support sans les exécuter',
  'support.reply.approve': 'Valider une réponse de support exacte',
  'support.repair.approve': 'Valider une réparation de sauvegarde exacte',
  'support.scenario.approve': 'Valider une modification de scénario exacte',
  'support.analysis.request': 'Demander une analyse de support',
  'backoffice.finance.read': 'Lire le reporting financier',
  'backoffice.finance.refresh': 'Rafraîchir le reporting financier',
  'backoffice.product.read': 'Lire le reporting produit',
  'backoffice.product.refresh': 'Rafraîchir le reporting produit',
  'backoffice.churn.read': 'Lire le reporting de désabonnement',
  'backoffice.churn.generate': "Générer l'analyse de désabonnement",
  'backoffice.versions.read': "Lire le reporting des versions de l'app",
  'backoffice.versions.refresh': "Rafraîchir le reporting des versions de l'app",
  'backoffice.capabilities.read': 'Lire les capacités manquantes',
  'backoffice.capabilities.manage': 'Changer le statut des capacités manquantes',
  'backoffice.weekly.read': 'Lire le reporting hebdomadaire',
  'backoffice.weekly.generate': 'Générer le reporting hebdomadaire',
  'backoffice.moderation.read': 'Lire les dossiers de modération',
  'backoffice.moderation.act': 'Appliquer des actions de modération',
};

export function permissionDescription(permission: string, fallback?: string): string {
  return permissionDescriptionsFr[permission] || fallback || '';
}

export interface PermissionRow {
  key: string;
  permission: string;
  module: string;
  risk: PermissionDefinition['risk'];
  description: string;
  scopes: string;
  origin?: string;
}

export interface PermissionGroup {
  module: string;
  label: string;
  rows: PermissionRow[];
}

const moduleOrder = ['iam', 'backoffice', 'author', 'prompts', 'evals', 'support'];

function moduleRank(module: string): number {
  const index = moduleOrder.indexOf(module);
  return index === -1 ? moduleOrder.length : index;
}

export function groupPermissionRows(rows: PermissionRow[]): PermissionGroup[] {
  const groups = new Map<string, PermissionRow[]>();
  for (const row of rows) {
    const bucket = groups.get(row.module);
    if (bucket) bucket.push(row);
    else groups.set(row.module, [row]);
  }
  return [...groups.entries()]
    .map(([module, items]) => ({module, label: moduleLabels[module] || module, rows: items}))
    .sort((a, b) => moduleRank(a.module) - moduleRank(b.module) || a.module.localeCompare(b.module));
}

/** Lignes du catalogue complet, pour la sélection de permissions d'une version de profil. */
export function catalogueRows(definitions: PermissionDefinition[]): PermissionRow[] {
  return definitions.map(definition => ({
    key: definition.permission,
    permission: definition.permission,
    module: definition.module,
    risk: definition.risk,
    description: permissionDescription(definition.permission, definition.description),
    scopes: scopeKindsLabel(definition.scopeKinds),
  }));
}

/** Lignes d'un jeu de permissions nommé (version de profil) : périmètres possibles du catalogue. */
export function permissionSetRows(permissions: string[], definitions: PermissionDefinition[]): PermissionRow[] {
  const byPermission = new Map(definitions.map(definition => [definition.permission, definition]));
  return permissions.map(permission => {
    const definition = byPermission.get(permission);
    return {
      key: permission,
      permission,
      module: definition?.module || permission.split('.')[0],
      risk: definition?.risk || 'read',
      description: permissionDescription(permission, definition?.description),
      scopes: definition ? scopeKindsLabel(definition.scopeKinds) : '—',
    };
  });
}

/** Lignes des droits effectifs : le périmètre réellement accordé, et l'affectation qui l'apporte. */
export function effectiveRows(grants: Array<PermissionGrant & {assignmentId?: string; source?: 'admin'}>, definitions: PermissionDefinition[]): PermissionRow[] {
  const byPermission = new Map(definitions.map(definition => [definition.permission, definition]));
  return grants.map((grant, index) => {
    const definition = byPermission.get(grant.permission);
    return {
      key: `${grant.permission}-${index}`,
      permission: grant.permission,
      module: definition?.module || grant.permission.split('.')[0],
      risk: definition?.risk || 'read',
      description: permissionDescription(grant.permission, definition?.description),
      scopes: scopeLabel(grant.scope),
      origin: grant.source === 'admin' ? 'via le statut admin' : grant.assignmentId ? `via ${grant.assignmentId}` : undefined,
    };
  });
}

export function countSensitive(permissions: string[], definitions: PermissionDefinition[]): number {
  const byPermission = new Map(definitions.map(definition => [definition.permission, definition]));
  return permissions.filter(permission => byPermission.get(permission)?.risk === 'sensitive').length;
}

/* ------------------------------------------------------------------ capacité d'action */

export interface CapacityCheck {label: string; ok: boolean; icon: 'check' | 'alert' | 'clock'}
export interface CapacityState {
  ready: boolean;
  tone: 'ok' | 'ro';
  title: string;
  checks: CapacityCheck[];
  canReauthenticate: boolean;
  needsMfaEnrolment: boolean;
}

/**
 * Heure à laquelle l'authentification cesse d'être « récente ».
 * `auth_time` vient du jeton (`getIdTokenResult`) : rafraîchir le jeton ne le renouvelle pas.
 */
export function authExpiryLabel(authTimeMs: number | null | undefined, maxAuthAgeSeconds: number): string | null {
  if (!authTimeMs || !Number.isFinite(authTimeMs) || !Number.isFinite(maxAuthAgeSeconds) || maxAuthAgeSeconds <= 0) return null;
  return new Intl.DateTimeFormat('fr-FR', {hour: '2-digit', minute: '2-digit'}).format(new Date(authTimeMs + maxAuthAgeSeconds * 1000));
}

export function capacityState(
  policy: {emailVerified: boolean; mfaRequired: boolean; mfaSatisfied: boolean; reauthRequired: boolean; maxAuthAgeSeconds: number},
  authTimeMs: number | null,
): CapacityState {
  const expiry = policy.reauthRequired ? null : authExpiryLabel(authTimeMs, policy.maxAuthAgeSeconds);
  const mfaReady = !policy.mfaRequired || policy.mfaSatisfied;
  const recentAuthReady = policy.maxAuthAgeSeconds <= 0 || !policy.reauthRequired;
  const ready = policy.emailVerified && mfaReady && recentAuthReady;
  return {
    ready,
    tone: ready ? 'ok' : 'ro',
    title: ready ? 'Actions autorisées' : 'Lecture seule',
    checks: [
      {label: `E-mail ${policy.emailVerified ? 'vérifié' : 'non vérifié'}`, ok: policy.emailVerified, icon: policy.emailVerified ? 'check' : 'alert'},
      policy.mfaRequired
        ? {label: `MFA ${policy.mfaSatisfied ? 'vérifiée' : 'non vérifiée'}`, ok: policy.mfaSatisfied, icon: policy.mfaSatisfied ? 'check' : 'alert'}
        : {label: 'MFA facultative', ok: true, icon: 'check'},
      {
        label: policy.maxAuthAgeSeconds <= 0
          ? 'Session valide'
          : policy.reauthRequired
          ? `Authentification > ${Math.round(policy.maxAuthAgeSeconds / 60)} min`
          : expiry ? `Authentification récente (expire ${expiry})` : 'Authentification récente',
        ok: recentAuthReady,
        icon: policy.maxAuthAgeSeconds <= 0 ? 'check' : 'clock',
      },
    ],
    canReauthenticate: mfaReady && policy.maxAuthAgeSeconds > 0 && policy.reauthRequired,
    needsMfaEnrolment: policy.mfaRequired && !policy.mfaSatisfied,
  };
}

/* ------------------------------------------------------------------ historique */

export const auditKindLabels: Record<string, string> = {
  'staff.changed': 'Accès modifiés',
  'profile.created': 'Version de profil créée',
  'migration.bootstrap': 'Initialisation du registre (bootstrap)',
  'migration.rollback': 'Retour arrière de migration',
  'migration.admin-bootstrap': 'Nomination du premier admin',
  'migration.admin-recovery': 'Récupération de l’administration',
  'migration.admin-rollback': 'Retour arrière du statut admin',
};

export function auditKindLabel(kind: string): string {
  return auditKindLabels[kind] || `Événement ${kind}`;
}

export type AuditGroup = 'staff' | 'profile' | 'migration' | 'unknown';

export function auditGroup(kind: string): AuditGroup {
  const family = kind.split('.')[0];
  return family === 'staff' || family === 'profile' || family === 'migration' ? family : 'unknown';
}

const groupIcons: Record<AuditGroup, 'users' | 'branch' | 'shield' | 'alert'> = {
  staff: 'users',
  profile: 'branch',
  migration: 'shield',
  unknown: 'alert',
};

/** `before`/`after` peuvent être un objet, `null`, ou un tableau (dont `[null]` au bootstrap). */
export function normalizeSide<T>(value: unknown): T[] {
  if (value === null || value === undefined) return [];
  return (Array.isArray(value) ? value : [value]).filter((item): item is T => item !== null && item !== undefined);
}

function isStaffAccess(value: unknown): value is StaffAccess {
  return !!value && typeof value === 'object' && 'uid' in (value as Record<string, unknown>) && 'assignments' in (value as Record<string, unknown>);
}

function isProfileVersion(value: unknown): value is ProfileVersion {
  return !!value && typeof value === 'object' && 'profileId' in (value as Record<string, unknown>) && 'permissions' in (value as Record<string, unknown>);
}

export function changedFields(before: StaffAccess | null | undefined, after: StaffAccess | null | undefined): string[] {
  if (!before && after) return [`Accès créés (révision ${after.revision})`];
  if (before && !after) return ['Accès retirés'];
  if (!before || !after) return [];
  const changes: string[] = [];
  if (isAdmin(before) !== isAdmin(after)) changes.push(isAdmin(after)
    ? 'Admin attribué : tous les droits actuels et futurs' : 'Statut admin retiré');
  if (before.status !== after.status) changes.push(`Statut : ${statusLabel(before.status)} → ${statusLabel(after.status)}`);
  if (JSON.stringify(before.assignments) !== JSON.stringify(after.assignments)) changes.push('Affectations modifiées');
  if (JSON.stringify(before.delegation) !== JSON.stringify(after.delegation)) changes.push('Plafond de délégation modifié');
  return changes;
}

function staffSummary(staff: StaffAccess): string {
  return `${isAdmin(staff) ? 'Admin : tous les droits actuels et futurs · ' : ''}${statusLabel(staff.status)} · révision ${staff.revision} · ${staff.assignments.length} affectation${staff.assignments.length > 1 ? 's' : ''} · plafond ${staff.delegation.length}`;
}

export interface AuditSummary {
  label: string;
  group: AuditGroup;
  icon: 'users' | 'branch' | 'shield' | 'alert';
  targets: string[];
  changes: string[];
  beforeText: string;
  afterText: string;
  statusAfter: string | null;
}

/**
 * Lit un événement d'audit sans supposer sa forme : un `before:[null]` de bootstrap est une
 * absence d'état antérieur, jamais une suppression.
 */
export function describeAuditEvent(event: AuditEvent): AuditSummary {
  const group = auditGroup(event.kind);
  const before = normalizeSide<unknown>(event.before);
  const after = normalizeSide<unknown>(event.after);
  const beforeStaff = before.filter(isStaffAccess);
  const afterStaff = after.filter(isStaffAccess);
  const afterProfiles = after.filter(isProfileVersion);
  const profilesCreated = normalizeSide<string>((event as Record<string, unknown>).profilesCreated);

  const targets = event.targetUid
    ? [event.targetUid]
    : afterStaff.length ? afterStaff.map(staff => staff.uid)
    : afterProfiles.map(profile => `${profile.profileId} v${profile.version}`);

  const changes: string[] = [];
  if (group === 'migration') {
    for (const path of profilesCreated) changes.push(`Profil créé : ${profileLabelFromPath(path)}`);
    for (const staff of afterStaff) changes.push(`Compte initialisé : ${staff.uid} · ${staffSummary(staff)}`);
    if (!changes.length && event.kind === 'migration.rollback') changes.push('État antérieur restauré');
  } else if (group === 'profile') {
    for (const profile of afterProfiles) changes.push(`${profile.name} · ${profile.permissions.length} permission${profile.permissions.length > 1 ? 's' : ''}`);
  } else {
    changes.push(...changedFields(beforeStaff[0] || null, afterStaff[0] || null));
  }

  return {
    label: auditKindLabel(event.kind),
    group,
    icon: groupIcons[group],
    targets,
    changes,
    beforeText: beforeStaff.length ? beforeStaff.map(staffSummary).join(' · ') : 'Aucun accès staff antérieur',
    afterText: afterStaff.length ? afterStaff.map(staffSummary).join(' · ')
      : afterProfiles.length ? afterProfiles.map(profile => `${profile.profileId} v${profile.version} · ${profile.permissions.length} permissions`).join(' · ')
      : 'Aucun état conservé',
    statusAfter: afterStaff.length === 1 ? afterStaff[0].status : null,
  };
}

/** `staff_profiles/administrateur-complet/versions/1` → `administrateur-complet v1`. */
export function profileLabelFromPath(path: string): string {
  const segments = path.split('/').filter(Boolean);
  const versionIndex = segments.lastIndexOf('versions');
  if (versionIndex > 0 && segments[versionIndex + 1]) return `${segments[versionIndex - 1]} v${segments[versionIndex + 1]}`;
  return segments.at(-1) || path;
}

export interface AuditFilter {kind: string; text: string}

/** Filtres appliqués aux seuls événements déjà chargés : `GET /audit` n'accepte que limit/cursor. */
export function filterAuditEvents(events: AuditEvent[], filter: AuditFilter): AuditEvent[] {
  const needle = filter.text.trim().toLowerCase();
  return events.filter(event => {
    if (filter.kind !== 'all' && event.kind !== filter.kind) return false;
    if (!needle) return true;
    const summary = describeAuditEvent(event);
    return [event.id, event.kind, event.actorUid, event.reason, summary.label, ...summary.targets]
      .some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
  });
}

export function auditKindsPresent(events: AuditEvent[]): string[] {
  return [...new Set(events.map(event => event.kind))].sort();
}
